/**
 * DraftOptionsBar
 *
 * The thin strip above a draft modal's paper that carries the two inputs the
 * AI cannot infer:
 *
 *   • DATE — every template and every AI draft used to hardcode "today", so a
 *     resolution passed last Tuesday could not be drafted at all without hand-
 *     editing the rendered document. The date drives BOTH the local preview
 *     (getDocumentPreview) and the AI prompt, so the two never disagree.
 *   • INSTRUCTIONS — free-text steer for this one generation ("the appointee is
 *     Mr X, effective 1 April"). Collapsed by default; a teal dot on the toggle
 *     is the only sign it holds anything, so an unused field costs no space.
 *
 * Chrome matches SupportingDocUploader (same cream strip, same border, same
 * type scale) — the two stack directly on top of each other in both modals.
 *
 * Props:
 *   date               ISO "YYYY-MM-DD" (or '' for none)
 *   onDateChange       (iso) => void        — omit to hide the date control
 *   dateLabel          label text, default "Date"
 *   instructions       current free-text value
 *   onInstructionsChange (text) => void     — omit to hide the instructions control
 *   disabled           disables both while a draft is generating
 */

import { useState } from 'react';
import { FiCalendar, FiMessageSquare } from 'react-icons/fi';
import { DateField, textareaClass } from './FormControls';

const STRIP_STYLE = { background: '#faf9f6', borderColor: '#ddd6c6' };

const INSTRUCTIONS_MAX = 1200;

export default function DraftOptionsBar({
  date,
  onDateChange,
  dateLabel = 'Date',
  instructions = '',
  onInstructionsChange,
  disabled = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const showDate = typeof onDateChange === 'function';
  const showInstructions = typeof onInstructionsChange === 'function';
  if (!showDate && !showInstructions) return null;

  const hasInstructions = Boolean(String(instructions || '').trim());

  return (
    <div className="border-b" style={STRIP_STYLE}>
      <div className="px-4 py-[5px] flex items-center gap-2">
        {showDate && (
          <>
            <FiCalendar size={11} className="text-[#7a7060] shrink-0" />
            <span className="text-[11px] text-[#7a7060] shrink-0">{dateLabel}</span>
            <div className="w-[168px] shrink-0">
              <DateField
                size="sm"
                value={date || ''}
                onChange={onDateChange}
                disabled={disabled}
                placeholder="Select date"
              />
            </div>
            <span className="text-[9.5px] text-[#a09880] hidden sm:inline">
              Used in the preview and the AI draft
            </span>
          </>
        )}

        {showInstructions && (
          <button
            type="button"
            onClick={() => setExpanded((s) => !s)}
            className="ml-auto inline-flex items-center gap-[6px] px-[8px] py-[3px] rounded-lg border text-[10.5px] font-semibold transition-colors hover:border-[#1e8a8a]"
            style={{ borderColor: '#ddd6c6', background: '#fff', color: '#3a3020' }}
            aria-expanded={expanded}
          >
            <FiMessageSquare size={11} className="text-[#7a7060]" />
            Instructions for the AI
            {hasInstructions && (
              <span className="w-[6px] h-[6px] rounded-full" style={{ background: '#145f5f' }} />
            )}
            <svg
              width="10" height="10" viewBox="0 0 10 10"
              className={`shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
              fill="none" stroke="#7a7060" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M2.5 3.5L5 6.5L7.5 3.5" />
            </svg>
          </button>
        )}
      </div>

      {showInstructions && expanded && (
        // Bootstrap's !important utilities clobber Tailwind's p-*/py-* here, so
        // the padding is an arbitrary value.
        <div className="px-4 pb-[8px]">
          <textarea
            rows={2}
            maxLength={INSTRUCTIONS_MAX}
            className={`${textareaClass} min-h-[58px] text-[12px]`}
            value={instructions || ''}
            disabled={disabled}
            placeholder="Optional — e.g. “Appoint Mr. A. Sharma (DIN 01234567) as Managing Director for 5 years w.e.f. 1 April 2026; remuneration ₹24,00,000 p.a.”"
            onChange={(e) => onInstructionsChange(e.target.value.slice(0, INSTRUCTIONS_MAX))}
          />
          <div className="mt-[3px] text-[9.5px] text-[#a09880]">
            Applies to the next AI draft only — it is not saved with the document.
          </div>
        </div>
      )}
    </div>
  );
}
